import * as React from 'react'
import { FormikProps } from 'formik'
import { FormikTextField } from 'components'
import { InfoOutlined, Search } from 'cs-kevlar-react/lib/es/icons'
import {
  Box,
  OutlinedButton,
  Typography,
  Tooltip,
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  Input,
  Autocomplete,
} from 'cs-kevlar-react'
import * as Yup from 'yup'
import {
  makeStyles,
  createStyles,
  Theme,
  useTheme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { CustomerAdmin } from 'contexts/base'

interface EditAdminUserFormProps {
  formikProps: FormikProps<CustomerAdmin>
  allowSearch: boolean
  onSearch: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridItem: {
      width: '100%',
    },
    disabledMultiline: {
      '& .MuiInput-underline.Mui-disabled:before': {
        borderBottomStyle: 'solid',
      },
    },
    labelIcon: {
      marginLeft: theme.spacing(2),
    },
    nameRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    name: {
      flexGrow: 1,
    },
    searchButton: {
      marginLeft: theme.spacing(8),
    },
    tooltip: {
      marginLeft: theme.spacing(4),
    },
    label: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      '& .MuiTypography-body1': {
        lineHeight: '16px',
      },
    },
    removePadding: {
      '& .MuiAutocomplete-inputRoot': {
        paddingRight: '0',
      },
    },
  }),
)

export const EditAdminUserFormSchema = Yup.object().shape({
  name: Yup.string(),
  fullName: Yup.string(),
  phone: Yup.string().required('The Phone field is required.').nullable(),
  cadEmail: Yup.string().nullable(),
  email: Yup.string()
    .email('The Email field must be a valid email address.')
    .required('The Email field is required.')
    .nullable(),
})

export const EditAdminUserForm: React.FunctionComponent<EditAdminUserFormProps> = ({
  formikProps,
  allowSearch,
  onSearch,
}) => {
  const classes = useStyles({})
  const theme = useTheme<Theme>()

  const hasPhoneError = formikProps.touched.phone && !!formikProps.errors.phone

  const emailOptions: string[] = formikProps.values.cadEmails
    ? formikProps.values.cadEmails
    : []

  return (
    <React.Fragment>
      <Grid item className={classes.gridItem}>
        <Box className={classes.nameRow}>
          <Box className={classes.name}>
            <FormikTextField
              formikProps={formikProps}
              fieldName="fullName"
              label="Name"
              fullWidth
              readOnly
            />
          </Box>

          {allowSearch && (
            <OutlinedButton onClick={onSearch} className={classes.searchButton}>
              <Search />
            </OutlinedButton>
          )}
        </Box>
      </Grid>
      <Grid item className={classes.gridItem}>
        <FormikTextField
          formikProps={formikProps}
          fieldName="name"
          label="Username"
          fullWidth
          readOnly
        />
      </Grid>
      <Grid item className={classes.gridItem}>
        <FormControl error={hasPhoneError}>
          <InputLabel className={classes.label} htmlFor="phone" shrink>
            <Typography display="inline">Phone</Typography>
            <Tooltip
              placement="top"
              title="Phone number is required for Advanced Authentication."
              aria-label="Phone number is required for Advanced Authentication."
              arrow
              className={classes.tooltip}
            >
              <InfoOutlined />
            </Tooltip>
          </InputLabel>
          <Input
            id="phone"
            name="phone"
            value={formikProps.values.phone}
            onChange={formikProps.handleChange}
            onBlur={formikProps.handleBlur}
            fullWidth
            required
            error={hasPhoneError}
          />
          {!hasPhoneError && <FormHelperText></FormHelperText>}
          {hasPhoneError && (
            <FormHelperText error>{formikProps.errors.phone}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item className={classes.gridItem}>
        <Autocomplete
          id="email"
          className={classes.removePadding}
          freeSolo={true}
          disableClearable={true}
          options={emailOptions}
          defaultValue={formikProps.values.email ?? emailOptions[0] ?? ''}
          onChange={(event, newValue) => {
            formikProps.setFieldValue('email', newValue)
          }}
          onBlur={formikProps.handleBlur}
          renderInput={(params) => (
            <FormikTextField
              {...params}
              InputLabelProps={{
                ...params.InputLabelProps,
                ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
              }}
              label="Email"
              formikProps={formikProps}
              fieldName="email"
              type="email"
              fullWidth
              required
            />
          )}
        />
      </Grid>
    </React.Fragment>
  )
}
